<script setup>
import { ref, reactive, onMounted } from 'vue'
import { EaseChatClient } from '@/IM/initwebsdk'
import { handleSDKErrorNotifi } from '@/utils/handleSomeData'
import { captchaImage, loginCustomNew, getImToken } from '@/api/custom'
import { useStore } from 'vuex'
import { usePlayRing } from '@/hooks'
import { ElMessage } from 'element-plus'
import { setToken, removeToken } from '@/utils/auth'
import resetPassword from '../resetPassword.vue'

const REMEMBERED_ACCOUNT_KEY = 'IM_rememberedAccount'
const store = useStore()
const formRef = ref(null)
const resetPasswordRef = ref(null)
const buttonLoading = ref(false)
const rememberAccount = ref(false)

const loginValue = reactive({
  username: '',
  password: '',
  code: '',
  uuid: ''
})

const state = reactive({
  codeImg: ''
})

const rules = reactive({
  username: [{ required: true, message: '请输入账号', trigger: 'blur' }],
  password: [{ required: true, message: '请输入密码', trigger: 'blur' }],
  code: [{ required: true, message: '请输入验证码', trigger: 'change' }]
})

onMounted(() => {
  window.localStorage.removeItem('hxUserToken')
  window.localStorage.removeItem('customUserInfo')
  window.localStorage.removeItem('EASEIM_loginUser')
  window.localStorage.removeItem('customToken')
  removeToken()
  EaseChatClient.close()

  const rememberedAccount = window.localStorage.getItem(REMEMBERED_ACCOUNT_KEY)
  if (rememberedAccount) {
    loginValue.username = rememberedAccount
    rememberAccount.value = true
  }
  getImg()
})

const getImg = () => {
  captchaImage().then((res) => {
    state.codeImg = 'data:image/gif;base64,' + res.img
    loginValue.uuid = res.uuid
  })
}

const submitLogin = () => {
  formRef.value.validate((valid) => {
    if (valid) loginIM()
  })
}

const loginIM = async () => {
  const { clickRing } = usePlayRing()
  clickRing()
  buttonLoading.value = true

  if (rememberAccount.value) {
    window.localStorage.setItem(REMEMBERED_ACCOUNT_KEY, loginValue.username)
  } else {
    window.localStorage.removeItem(REMEMBERED_ACCOUNT_KEY)
  }

  loginCustomNew(loginValue)
    .then(async (res) => {
      window.localStorage.setItem('customToken', JSON.stringify(res.token))
      setToken(res.token)
      if (res.code == 200) {
        store.dispatch('GetInfo')
        getImToken()
          .then(async (res2) => {
            try {
              const userInfos = await store.dispatch('getCustomUserInfo')
              window.localStorage.setItem('customUserInfo', JSON.stringify(userInfos.data))
              try {
                const { accessToken } = await EaseChatClient.open({
                  user: userInfos.data.hxusername.toLowerCase(),
                  accessToken: res2.msg
                })
                window.localStorage.setItem(
                  'EASEIM_loginUser',
                  JSON.stringify({
                    user: userInfos.data.hxusername,
                    accessToken
                  })
                )
              } catch (error) {
                const extraInfo = error?.data?.extraInfo
                handleSDKErrorNotifi(error.type, extraInfo?.errDesc || error.message)
                EaseChatClient.close()
                getImg()
                buttonLoading.value = false
              } finally {
                buttonLoading.value = false
              }
            } catch (error) {
              getImg()
              buttonLoading.value = false
              ElMessage({
                type: 'error',
                message: '登录出错，请刷新重试！',
                center: true
              })
            }
          })
          .catch((error) => {
            getImg()
            buttonLoading.value = false
            ElMessage({
              type: 'error',
              message: error.msg || error.message,
              center: true
            })
          })
      } else {
        getImg()
        buttonLoading.value = false
        ElMessage({
          type: 'error',
          message: res.msg,
          center: true
        })
      }
    })
    .catch((error) => {
      getImg()
      buttonLoading.value = false
      ElMessage({
        type: 'error',
        message: error.msg || error.message,
        center: true
      })
    })
}

const openResetPsd = () => {
  resetPasswordRef.value.open()
}
</script>

<template>
    <el-form
        ref="formRef"
        :model="loginValue"
        :rules="rules"
        class="login-form"
        @keyup.enter="submitLogin"
    >
        <el-form-item prop="username">
            <el-input
                v-model="loginValue.username"
                autocomplete="username"
                placeholder="账号"
                clearable
            >
                <template #prefix><User class="input-icon" /></template>
            </el-input>
        </el-form-item>

        <el-form-item prop="password">
            <el-input
                v-model="loginValue.password"
                type="password"
                autocomplete="current-password"
                placeholder="密码"
                show-password
            >
                <template #prefix><Lock class="input-icon" /></template>
            </el-input>
        </el-form-item>

        <el-form-item prop="code" class="captcha-form-item">
            <div class="captcha-row">
                <el-input
                    v-model="loginValue.code"
                    autocomplete="off"
                    placeholder="验证码"
                >
                    <template #prefix><Key class="input-icon" /></template>
                </el-input>
                <button class="captcha-image" type="button" title="点击刷新验证码" @click="getImg">
                    <img v-if="state.codeImg" :src="state.codeImg" alt="验证码" />
                </button>
            </div>
        </el-form-item>

        <div class="login-options">
            <el-checkbox v-model="rememberAccount">记住账号</el-checkbox>
            <button class="forgot-password" type="button" @click="openResetPsd">忘记密码?</button>
        </div>

        <el-form-item class="submit-form-item">
            <el-button type="primary" :loading="buttonLoading" @click.prevent="submitLogin">
                <span v-if="!buttonLoading">登录</span>
                <span v-else>登录中...</span>
            </el-button>
        </el-form-item>

        <p class="security-tip">安全登录&nbsp;&nbsp;·&nbsp;&nbsp;信息加密保护</p>
        <resetPassword ref="resetPasswordRef" />
    </el-form>
</template>

<style lang="scss" scoped>
.login-form {
    :deep(.el-form-item) {
        margin-bottom: 18px;
    }

    :deep(.el-input__wrapper) {
        width: 100%;
        height: 62px;
        padding: 0 19px 0 23px;
        border: 1px solid #c8d5e8;
        border-radius: 10px;
        background: rgba(255, 255, 255, 0.52);
        box-shadow: none;
        box-sizing: border-box;
        transition: border-color 0.2s ease, box-shadow 0.2s ease;
    }

    :deep(.el-input__wrapper:hover) {
        border-color: #72d5d0;
    }

    :deep(.el-input__wrapper.is-focus) {
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(13, 200, 193, 0.11);
    }

    :deep(.el-input__inner) {
        height: 60px;
        font-size: 20px;
        color: #23325c;
    }

    :deep(.el-input__inner::placeholder) {
        color: #8291b5;
    }

    :deep(.el-input__prefix) {
        margin-right: 17px;
    }

    :deep(.el-input__suffix) {
        color: #8f9dbd;
        font-size: 21px;
    }

    :deep(.el-form-item__error) {
        padding-top: 3px;
    }
}

.input-icon {
    width: 25px;
    height: 25px;
    color: var(--primary);
}

.captcha-form-item {
    :deep(.el-form-item__content) {
        display: block;
    }
}

.captcha-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 170px;
    gap: 14px;
}

.captcha-image {
    height: 62px;
    padding: 0;
    overflow: hidden;
    border: 1px solid #c8d5e8;
    border-radius: 10px;
    background: #f4f8fb;
    cursor: pointer;

    img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}

.login-options {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 1px 0 24px;

    :deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
        border-color: var(--primary);
        background-color: var(--primary);
    }

    :deep(.el-checkbox__inner) {
        width: 23px;
        height: 23px;
        border-radius: 5px;
    }

    /* 不覆盖 top/left，保留库自带的 left:50%/top:50% + translate 自动居中，
       仅放大对勾尺寸与笔画以匹配 23px 的方框 */
    :deep(.el-checkbox__inner::after) {
        width: 5px;
        height: 11px;
        border-width: 2px;
    }

    :deep(.el-checkbox__label) {
        padding-left: 10px;
        font-size: 17px;
        color: #26365c;
    }
}

.forgot-password {
    padding: 4px 0;
    border: 0;
    background: transparent;
    font-size: 17px;
    color: var(--primary-dark);
    cursor: pointer;
}

.forgot-password:hover {
    color: #078f8a;
}

.submit-form-item {
    margin-bottom: 0 !important;

    :deep(.el-button) {
        width: 100%;
        height: 66px;
        margin: 0;
        border: 0;
        border-radius: 10px;
        background: linear-gradient(100deg, #13cfc7 0%, #08c4bd 100%);
        box-shadow: 0 10px 22px rgba(13, 200, 193, 0.2);
        font-size: 22px;
        font-weight: 600;
        letter-spacing: 4px;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    :deep(.el-button:hover) {
        background: linear-gradient(100deg, #0fc1ba 0%, #04b4ae 100%);
        box-shadow: 0 12px 25px rgba(13, 178, 172, 0.26);
        transform: translateY(-1px);
    }
}

.security-tip {
    margin: 26px 0 0;
    text-align: center;
    font-size: 17px;
    line-height: 1.4;
    color: #8698bc;
}

@media (max-width: 520px) {
    .login-form {
        :deep(.el-input__wrapper) {
            height: 55px;
            padding-right: 15px;
            padding-left: 17px;
        }

        :deep(.el-input__inner) {
            height: 53px;
            font-size: 17px;
        }

        :deep(.el-input__prefix) {
            margin-right: 12px;
        }
    }

    .captcha-row {
        grid-template-columns: minmax(0, 1fr) 124px;
        gap: 9px;
    }

    .captcha-image {
        height: 55px;
    }

    .login-options {
        margin-bottom: 20px;

        :deep(.el-checkbox__label),
        .forgot-password {
            font-size: 15px;
        }
    }

    .submit-form-item :deep(.el-button) {
        height: 57px;
        font-size: 20px;
    }

    .security-tip {
        margin-top: 21px;
        font-size: 14px;
    }
}

/* 矮视口（笔记本小屏、系统缩放后高度不足）：压缩表单纵向留白，保证完整显示 */
@media (min-width: 901px) and (max-height: 760px) {
    .login-form {
        :deep(.el-form-item) {
            margin-bottom: 13px;
        }
    }

    .login-options {
        margin: 0 0 16px;
    }

    .security-tip {
        margin-top: 16px;
    }
}

/* 极矮视口：进一步收拢表单控件高度 */
@media (min-width: 901px) and (max-height: 560px) {
    .login-form {
        :deep(.el-input__wrapper) {
            height: 56px;
        }

        :deep(.el-input__inner) {
            height: 54px;
            font-size: 18px;
        }
    }

    .captcha-image {
        height: 56px;
    }

    .submit-form-item :deep(.el-button) {
        height: 56px;
        font-size: 20px;
    }

    .security-tip {
        margin-top: 12px;
    }
}

/* 横屏矮视口（手机横屏等）：表单改为紧凑布局，减少页面滚动 */
@media (max-width: 900px) and (max-height: 560px) {
    .login-form {
        :deep(.el-form-item) {
            margin-bottom: 11px;
        }

        :deep(.el-input__wrapper) {
            height: 50px;
            padding-right: 14px;
            padding-left: 16px;
        }

        :deep(.el-input__inner) {
            height: 48px;
            font-size: 17px;
        }

        :deep(.el-input__prefix) {
            margin-right: 12px;
        }
    }

    .captcha-row {
        grid-template-columns: minmax(0, 1fr) 120px;
        gap: 10px;
    }

    .captcha-image {
        height: 50px;
    }

    .login-options {
        margin: 0 0 12px;

        :deep(.el-checkbox__label),
        .forgot-password {
            font-size: 15px;
        }
    }

    .submit-form-item :deep(.el-button) {
        height: 50px;
        font-size: 19px;
    }

    .security-tip {
        margin-top: 12px;
        font-size: 14px;
    }
}
</style>
